import { useQuery } from 'react-query';
import axios from 'axios';
import { TerminalAppearanceAPI } from '../../../constants/API';
import {
  TerminalAppearanceResponse,
  TerminalTabloSettings,
  TerminalTabloSettingsFromBackend
} from '../../../types/Admin/TerminalAppearanceTypes';
import { commonHeaders } from '../../../constants/HeadersAPI';
import { baseSoundURL } from '../../../config';

const normalizeMediaUrl = (url: string) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const separator = url.startsWith('/') ? '' : '/';
  return `${baseSoundURL}${separator}${url}`;
};

const normalizeButtonImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  return normalizeMediaUrl(url);
};

const normalizeTabloShape = (
  tablo: TerminalTabloSettings | TerminalTabloSettingsFromBackend | null | undefined
): TerminalTabloSettings | null | undefined => {
  if (tablo == null) return tablo as null | undefined;
  if ('elements' in tablo && tablo.elements) return tablo as TerminalTabloSettings;
  const flat = tablo as TerminalTabloSettingsFromBackend;
  return {
    ...flat,
    elements: {
      header: flat.header ?? ({} as TerminalTabloSettings['elements']['header']),
      video: flat.video ?? ({} as TerminalTabloSettings['elements']['video']),
      title: flat.title ?? ({} as TerminalTabloSettings['elements']['title']),
      queue: flat.queue ?? ({} as TerminalTabloSettings['elements']['queue']),
      takenTickets: flat.takenTickets ?? ({} as TerminalTabloSettings['elements']['takenTickets']),
      ticker: flat.ticker ?? ({} as TerminalTabloSettings['elements']['ticker']),
      slider: flat.slider ?? ({} as TerminalTabloSettings['elements']['slider']),
      weatherWidget:
        flat.weatherWidget ?? ({} as TerminalTabloSettings['elements']['weatherWidget']),
      rssBeltaWidget:
        flat.rssBeltaWidget ?? ({} as TerminalTabloSettings['elements']['rssBeltaWidget'])
    }
  };
};

export type UseGetTerminalAppearanceParams = {
  /** If set, load settings for this queue; otherwise default settings */
  branchQueueId?: number | null;
};

export const useGetTerminalAppearance = (params?: UseGetTerminalAppearanceParams) => {
  const branchQueueId = params?.branchQueueId ?? null;
  const {
    data: terminalAppearanceResponse,
    isFetching: isLoadingTerminalAppearance,
    isSuccess: isTerminalAppearanceReceived
  } = useQuery(
    ['TerminalAppearance', branchQueueId],
    () =>
      axios.get<TerminalAppearanceResponse>(TerminalAppearanceAPI.getAppearance, {
        ...commonHeaders(),
        params: branchQueueId != null ? { branchQueueId } : undefined
      }),
    {
      select: ({ data }) => ({
        ...data,
        settings: data.settings
          ? {
              ...data.settings,
              buttons: data.settings.buttons
                ? {
                    ...data.settings.buttons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      data.settings.buttons.ticketOuterCircleSmallUrl
                    )
                  }
                : data.settings.buttons,
              sectionButtons: data.settings.sectionButtons
                ? {
                    ...data.settings.sectionButtons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      data.settings.sectionButtons.ticketOuterCircleSmallUrl
                    )
                  }
                : data.settings.sectionButtons,
              citoButtons: data.settings.citoButtons
                ? {
                    ...data.settings.citoButtons,
                    ticketOuterCircleSmallUrl: normalizeButtonImageUrl(
                      data.settings.citoButtons.ticketOuterCircleSmallUrl
                    )
                  }
                : data.settings.citoButtons,
              tablo: normalizeTabloShape(
                data.settings?.tablo as TerminalTabloSettingsFromBackend | undefined
              )
            }
          : data.settings,
        backgrounds: (data.backgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        })),
        childrenBackgrounds: (data.childrenBackgrounds || []).map((background) => ({
          ...background,
          url: normalizeMediaUrl(background.url)
        })),
        ticketPictures: (data.ticketPictures || []).map((picture) => ({
          ...picture,
          url: normalizeMediaUrl(picture.url)
        })),
        ticketLayoutImages: (data.ticketLayoutImages || []).map((picture) => ({
          ...picture,
          url: normalizeMediaUrl(picture.url)
        }))
      })
    }
  );

  return { terminalAppearanceResponse, isLoadingTerminalAppearance, isTerminalAppearanceReceived };
};
